import {
  Combobox,
  type ComboboxProps,
  type OptionOnSelectData,
  type SelectionEvents,
} from "@fluentui/react-components";
import { useControllableValue } from "@fluentui/react-hooks";
import * as React from "react";
import { useStyles } from "../PromptFilter.styles";
import { PromptFiltersSkeleton } from "../PromptFilterSkeleton";
import { usePromptFilterLocalizations } from "../usePromptFilterStrings";
import { groupFilters } from "../utils";
import type { FilterProps } from "./Filter.types";
import { RenderOptions } from "./RenderOptions";

/**
 * Combobox filter to select a single item using text input.
 * This component does not yet support multi-select.
 * @param param0 props
 * @returns ComboboxFilter
 */
export const ComboboxFilter: React.FC<FilterProps> = ({
  appearance,
  tab,
  filter,
  isLoading,
  selectedValues,
  defaultSelectedValues,
  onChange,
  onOpenChanged,
}) => {
  const styles = useStyles(appearance)();
  const [values, setValues] = useControllableValue(
    selectedValues,
    defaultSelectedValues,
    onChange
  );
  const {
    facetKeyToLabel,
    facetValueToLabel,
    selectedValueToLabel,
    groupValueToLabel,
  } = usePromptFilterLocalizations();
  const filterLabel = facetKeyToLabel(filter.key);
  const filterByGroup = groupFilters(filter);
  const displayedValueText =
    values && values.length > 0
      ? values
          .map((value: string) => {
            const filterOption = filter.options.find(
              (option) => option.value === value
            );
            if (filterOption?.displayLabel) {
              return filterOption.displayLabel;
            }

            return selectedValueToLabel(value);
          })
          .join(", ")
      : "";

  const onChanged = (_: SelectionEvents, data: OptionOnSelectData): void => {
    setValues(data.selectedOptions);
  };

  const handleOnOpenChanged: ComboboxProps["onOpenChange"] = React.useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (_e: any, data: any) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      onOpenChanged && onOpenChanged(data.open, [filter]);
    },
    [filter, onOpenChanged]
  );

  if (filter.options.length < 1) {
    return null;
  }

  return (
    <React.Fragment key={`${tab}-${filter.key}`}>
      {isLoading ? (
        <PromptFiltersSkeleton />
      ) : (
        <Combobox
          data-testid={`${filter.key}-combobox`}
          aria-label={filterLabel}
          className={styles.dropdown}
          multiselect={!filter.isSingleSelect}
          placeholder={filterLabel}
          defaultValue={displayedValueText}
          defaultSelectedOptions={values ?? []}
          onOptionSelect={onChanged}
          onOpenChange={handleOnOpenChanged}
          inlinePopup={true}
          style={{ zIndex: 2 }} // Added z-index style so box doesn't get hidden behind other elements
          listbox={
            filter.isOverflowItemsFrom10 && {
              className: styles.filterListBoxOverflowFrom10Items,
            }
          }
          positioning={
            filter.width === "wide"
              ? {
                  autoSize: "width",
                }
              : {}
          }
        >
          {Object.entries(filterByGroup).map(([groupKey, filterOptions]) => (
            <React.Fragment key={groupKey}>
              <RenderOptions
                groupKey={groupKey}
                groupLabel={groupValueToLabel(groupKey)}
                filterOptions={filterOptions}
                selectedValues={values ?? []}
                valueToLabel={facetValueToLabel}
                appearance={appearance}
              />
            </React.Fragment>
          ))}
        </Combobox>
      )}
    </React.Fragment>
  );
};
